import { useCallback, useEffect, useState } from "react";

import { toError, type AsyncState } from "@/application/hooks/async-state";
import type { Lead, LeadStatus } from "@/domain/models/lead";
import type { CreateLeadInput } from "@/domain/repositories/lead.repository";
import { repositories } from "@/infrastructure/repositories";

export function useLeads() {
  const [state, setState] = useState<AsyncState<Lead[]>>({
    status: "loading",
    data: null,
    error: null,
  });

  const reload = useCallback(async () => {
    setState({ status: "loading", data: null, error: null });
    try {
      const data = await repositories.leads.list();
      if (data.length === 0) {
        setState({ status: "empty", data: null, error: null });
        return;
      }
      setState({ status: "success", data, error: null });
    } catch (error) {
      setState({ status: "error", data: null, error: toError(error) });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updateStatus = useCallback(
    async (id: string, status: LeadStatus) => {
      await repositories.leads.updateStatus(id, status);
      setState((prev) => {
        if (prev.status !== "success" || !prev.data) return prev;
        return {
          ...prev,
          data: prev.data.map((lead) =>
            lead.id === id ? { ...lead, status, updatedAt: new Date().toISOString() } : lead,
          ),
        };
      });
    },
    [],
  );

  const create = useCallback(async (input: CreateLeadInput) => {
    const lead = await repositories.leads.create(input);
    setState((prev) => {
      if (prev.status === "success" && prev.data) {
        return { ...prev, data: [lead, ...prev.data] };
      }
      return { status: "success", data: [lead], error: null };
    });
    return lead;
  }, []);

  return { ...state, reload, updateStatus, create };
}
